import { defineStore } from "pinia";
import { useQuery } from "@vue/apollo-composable";
import { andcAccountLogin, clientLogout, validateSessionToken } from "@/graphql/accounts";
import { session } from "@/utils/users";

const SESSION_TOKEN_KEY = 'session_token';
const SESSION_EXPIRY_KEY = 'session_expiry';
const USER_DATA_KEY = 'user_data';

export const useAuth = defineStore('auth', {
    state: () => {
        return {
            user: null,
            session_token: null,
            is_authenticated: false,
            loading: false,
            error: null
        };
    },

    getters: {
        isLoggedIn(state) {
            return state.is_authenticated && state.session_token !== null;
        },
        
        getSessionToken: (state) => {
            return state.session_token;
        },
        
        isClient(state) {
            return state.is_authenticated;
        }
    },

    actions: {
        // Initialize auth state from localStorage
        initAuth() {
            const token = session.get(SESSION_TOKEN_KEY);
            const expiry = session.get(SESSION_EXPIRY_KEY);
            const user_data = session.get(USER_DATA_KEY);
            
            if (token && expiry && user_data) {
                const now = Date.now();
                if (now < expiry) {
                    this.session_token = token;
                    this.user = user_data;
                    this.is_authenticated = true;
                } else {
                    this.logout();
                }
            }
        },

        // Check if session is expired
        isSessionExpired() {
            const expiry = session.get(SESSION_EXPIRY_KEY);
            if (!expiry) return true;
            
            const now = Date.now();
            return now >= expiry;
        },

        // Login method
        async login(email, password) {
            this.loading = true;
            this.error = null;

            return new Promise((resolve, reject) => {
                const query = useQuery(andcAccountLogin, {
                    email: email,
                    password: password
                });

                query.onError((error) => {
                    this.error = error.message || 'Login failed';
                    this.loading = false;
                    reject({ success: false, error: this.error });
                });
                
                query.onResult((result) => {
                    if (result.data?.andcAccountLogin) {
                        const { client, session_token } = result.data.andcAccountLogin;
                        
                        // Only clients allowed
                        if (!client) {
                            this.error = 'Erreur lors de la connexion. Veuillez vérifier votre courriel et mot de passe.';
                            this.loading = false;
                            reject({ success: false, error: this.error });
                            return;
                        }
                        
                        // Set session expiry to 3 hours from now
                        const expiry = Date.now() + (3 * 60 * 60 * 1000); // 3 hours in milliseconds
                        
                        // Store in localStorage
                        session.set(SESSION_TOKEN_KEY, session_token);
                        session.set(SESSION_EXPIRY_KEY, expiry);
                        session.set(USER_DATA_KEY, client);
                        
                        // Update store state
                        this.session_token = session_token;
                        this.user = client;
                        this.is_authenticated = true;
                        this.loading = false;
                        
                        resolve({ success: true, user: client });
                    } else {
                        this.error = 'Login failed - no data received';
                        this.loading = false;
                        reject({ success: false, error: this.error });
                    }
                });
            });
        },

        // Logout method
        async logout() {
            const token = this.session_token || session.getSessionToken();
            
            // Clear session data and store state immediately
            session.clearAuth();
            this.user = null;
            this.session_token = null;
            this.is_authenticated = false;
            this.error = null;
            
            // Try to notify the server, but don't wait for it
            if (token) {
                return new Promise((resolve) => {
                    const query = useQuery(clientLogout, {
                        session_token: token,
                    });

                    query.onError(() => {
                        // Even if server logout fails, we've already cleared locally
                        resolve({ success: true });
                    });
                    
                    query.onResult((result) => {
                        const { status, message } = result.data.clientLogout;
                        resolve({ success: true, status: status, message: message });
                    });
                });
            }
            
            return Promise.resolve({ success: true });
        },

        // Refresh session if needed
        refreshSession() {
            if (this.isSessionExpired()) {
                this.logout();
                return false;
            }

            return true;
        },

        // Get session token for API calls
        getAuthToken() {
            if (this.isSessionExpired()) {
                this.logout();
                return null;
            }

            return this.session_token;
        },

        // Validate session token with the server
        async validateSession() {
            if (!this.session_token) {
                return { valid: false };
            }

            return new Promise((resolve) => {
                const query = useQuery(validateSessionToken, {
                    session_token: this.session_token
                });

                query.onError((error) => {
                    // On network/server errors, don't logout - just return invalid
                    // The session might still be valid, just can't reach the server
                    console.error('Session validation error:', error);
                    resolve({ valid: false, error: error.message });
                });
                
                query.onResult((result) => {
                    const data = result.data?.validateSessionToken;

                    if (data?.client) {
                        resolve({ valid: true, client: data.client });
                    } else {
                        this.logout();
                        resolve({ valid: false });
                    }
                });
            });
        },

        updateUser(client) {
            const fullname = [client.firstname, client.lastname].filter(Boolean).join(' ');

            this.user = {
                ...this.user,
                ...client,
                fullname: fullname || this.user?.fullname,
            };

            session.set(USER_DATA_KEY, this.user);
        },

        async fetchClientProfile() {
            const token = this.getAuthToken();

            if (!token) {
                return { success: false, error: 'No session token available' };
            }

            return new Promise((resolve, reject) => {
                const query = useQuery(validateSessionToken, {
                    session_token: token,
                });

                query.onError((error) => {
                    reject({ success: false, error: error.message || 'Failed to fetch profile' });
                });

                query.onResult((result) => {
                    const client = result.data?.validateSessionToken?.client;

                    if (client) {
                        resolve({ success: true, client });
                    } else {
                        this.logout();
                        reject({ success: false, error: 'Session invalide' });
                    }
                });
            });
        },
    }
}); 